import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Table, { RowCell } from 'components/Table';
import { fetchQuery, graphql } from 'react-relay';
import moment from 'moment';
import Button from 'components/Button/Button';
import { DocumentsTable_delegate } from './__generated__/DocumentsTable_delegate.graphql';
import environment from 'api/environment';
import {
  ActivityTableQuery,
  ActivityTableQueryResponse
} from './__generated__/ActivityTableQuery.graphql';
import ActivityName from 'components/Delegate/ActivityName';
import { activityTitle } from '../utils/activity';
import ActiveType from 'components/Delegate/ActiveType';

const useStyles = createUseStyles((theme: Theme) => ({
  courseName: {
    fontWeight: 700,
    fontSize: '0.9rem'
  }
}));

const query = graphql`
  query ActivityTableQuery($uuid: UUID!, $offset: Int, $limit: Int) {
    delegate(uuid: $uuid) {
      firstName
      lastName
      activity(page: { offset: $offset, limit: $limit }) {
        edges {
          type
          createdAt
          course {
            ident: id
            name
          }
          classroomInstance {
            startDate
            endDate
          }
        }
        pageInfo {
          total
          limit
          given
          offset
        }
      }
    }
  }
`;

type Activity = NonNullable<
  NonNullable<ActivityTableQueryResponse['delegate']>['activity']
>;
type ActivityItem = NonNullable<Activity['edges']>[number];

type Props = {
  delegateUUID: string;
};

function ActivityTable({ delegateUUID }: Props) {
  const classes = useStyles();

  const [page, setPage] = React.useState({
    offset: 0,
    limit: 5
  });

  const [activity, setActivity] = React.useState<Activity>();
  const [delegate, setDelegate] = React.useState<{
    firstName: string;
    lastName: string;
  }>({
    firstName: '',
    lastName: ''
  });

  React.useEffect(() => {
    const variables = {
      uuid: delegateUUID,
      offset: page.offset,
      limit: page.limit
    };

    fetchQuery(environment, query, variables).then(
      (data: ActivityTableQueryResponse) => {
        console.log('data', data);

        if (data.delegate && data.delegate.activity) {
          setActivity(data.delegate.activity);
        }

        if (data.delegate) {
          setDelegate(data.delegate);
        }
      }
    );
  }, [page, delegateUUID]);

  const formatRow: RowCell<ActivityItem>[] = [
    {
      header: 'Activity Time',
      format: (data) => {
        return `${moment(data?.createdAt).format('hh:mm')} ${moment(
          data?.createdAt
        ).format('DD/MM/YYYY')}`;
      }
    },
    {
      header: 'Name',
      format: (data) => {
        if (!data) {
          return null;
        }

        const { title } = activityTitle(
          delegate.firstName,
          data.type as any,
          data.course?.name,
          data.classroomInstance ?? undefined
        );
        return (
          <ActivityName
            userName={`${delegate.firstName} ${delegate.lastName}`}
            title={title}
          />
        );
      }
    },
    {
      header: 'Name',
      format: (data) => {
        if (!data) {
          return null;
        }

        const { icon, iconText } = activityTitle(
          delegate.firstName,
          data.type as any,
          data.course?.name,
          data.classroomInstance ?? undefined
        );
        return <ActiveType icon={icon} text={iconText} />;
      }
    }
  ];

  return (
    <div>
      <h3>Activity</h3>
      <Table<ActivityItem>
        data={activity?.edges ?? []}
        pageInfo={activity?.pageInfo ?? undefined}
        onNextPage={() => {
          setPage({
            ...page,
            offset: page.offset + page.limit
          });
        }}
        onPreviousPage={() => {
          setPage({
            ...page,
            offset: page.offset - page.limit
          });
        }}
        formatRow={formatRow}
        tableName="activity"
        emptyTableText="No activity to show"
        fallbackLimit={5}
      />
    </div>
  );
}

export default ActivityTable;
